import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import olreadiaService from "services/olreadiaService";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function useBackgroundChecks() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const listForProfessionProfile = useCallback(async (professionProfileId, checkType) => {
    if (!professionProfileId) return [];
    setLoading(true);
    setError(null);
    try {
      const res = await olreadiaService.backgroundChecks.listForProfessionProfile(
        professionProfileId,
        checkType,
      );
      const nextChecks = Array.isArray(res) ? res : [];
      setChecks(nextChecks);
      return nextChecks;
    } catch (err) {
      const message = toMessage(err, "Failed to load background checks");
      setError(message);
      toast.error(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async (professionProfileId, checkType) => {
    return listForProfessionProfile(professionProfileId, checkType);
  }, [listForProfessionProfile]);

  const requestForProfessionProfile = useCallback(async (professionProfileId, payload, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.requestForProfessionProfile(
        professionProfileId,
        payload,
        actorId,
      );
      toast.success("Background check requested");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to request background check"));
      throw err;
    }
  }, []);

  const initiateForProfessionProfile = useCallback(async (professionProfileId, payload, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.initiateForProfessionProfile(
        professionProfileId,
        payload,
        actorId,
      );
      toast.success("Background check initiated");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to initiate background check"));
      throw err;
    }
  }, []);

  const createForProfessionProfile = useCallback(async (professionProfileId, payload, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.createForProfessionProfile(
        professionProfileId,
        payload,
        actorId,
      );
      toast.success("Background check created");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to create background check"));
      throw err;
    }
  }, []);

  const update = useCallback(async (backgroundCheckId, payload, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.update(
        backgroundCheckId,
        payload,
        actorId,
      );
      toast.success("Background check updated");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to update background check"));
      throw err;
    }
  }, []);

  const initiate = useCallback(async (backgroundCheckId, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.initiate(backgroundCheckId, actorId);
      toast.success("Background check moved to in progress");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to initiate background check"));
      throw err;
    }
  }, []);

  const cancel = useCallback(async (backgroundCheckId, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.cancel(backgroundCheckId, actorId);
      toast.success("Background check canceled");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to cancel background check"));
      throw err;
    }
  }, []);

  const submitItemResult = useCallback(async (backgroundCheckId, payload, actorId) => {
    try {
      const res = await olreadiaService.backgroundChecks.submitItemResult(
        backgroundCheckId,
        payload,
        actorId,
      );
      toast.success("Verification item saved");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to save verification item"));
      throw err;
    }
  }, []);

  return {
    checks,
    loading,
    error,
    refresh,
    listForProfessionProfile,
    requestForProfessionProfile,
    initiateForProfessionProfile,
    createForProfessionProfile,
    update,
    initiate,
    cancel,
    submitItemResult,
  };
}
